import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import SchemaOrg from '@/components/base/SchemaOrg';
import Breadcrumb from '@/components/base/Breadcrumb';
import { siteConfig } from '@/mocks/siteConfig';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypotValue = (formData.get('phone_alt') as string || '').trim();
    if (honeypotValue) {
      setFormStatus('success');
      setFormMessage('提交成功，我们会尽快回复您！');
      form.reset();
      return;
    }
    formData.delete('phone_alt');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9rdrtme3oq4jqumu060', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('提交成功，我们会尽快回复您！');
        form.reset();
      } else {
        setFormStatus('error');
        setFormMessage(parsed?.meta?.message || parsed?.message || '提交失败，请稍后重试');
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请稍后重试');
    }
  };

  return (
    <>
      <SchemaOrg type="Organization" />
      <SchemaOrg type="WebSite" />
      <SchemaOrg
        type="WebPage"
        extra={{ name: `关于我们 - 久久网`, description: '了解久久网的品牌故事、平台信誉与联系方式' }}
      />
      <SchemaOrg
        type="BreadcrumbList"
        extra={{
          items: [
            { name: '首页', url: 'https://njqxb.cn/' },
            { name: '关于我们', url: 'https://njqxb.cn/about' },
          ],
        }}
      />
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="w-full max-w-5xl mx-auto px-4 md:px-6">
            <Breadcrumb items={[{ name: '首页', url: '/' }, { name: '关于我们' }]} />

            <section className="mt-8 mb-16">
              <div className="text-center mb-12">
                <h1 className="text-3xl md:text-4xl font-bold text-foreground-950 font-[var(--font-heading)]">
                  关于久久网
                </h1>
                <p className="mt-4 text-base text-foreground-600 max-w-2xl mx-auto">
                  久久网 — 您信赖的综合性网络平台，汇聚海量优质内容资源
                </p>
              </div>

              <div className="bg-white rounded-2xl border border-background-200/70 p-8 md:p-10 mb-10">
                <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-[var(--font-heading)] mb-6">
                  品牌介绍
                </h2>
                <div className="space-y-4 text-sm md:text-base text-foreground-700 leading-relaxed">
                  <p>
                    久久网创立于{siteConfig.foundedYear}年，是一家专注于综合性内容服务的网络平台。自成立以来，久久网始终秉持
                    <strong>"汇聚优质内容，服务亿万用户"</strong>的理念，致力于为用户打造一个
                    覆盖多元领域、信息丰富全面、体验便捷流畅的一站式网络服务平台。
                  </p>
                  <p>
                    经过多年发展，久久网已建立起覆盖娱乐、资讯、科技、生活、教育、健康、财经、旅游等
                    <strong>八大核心领域</strong>的内容体系，累计发布精选文章超过
                    <strong>{siteConfig.stats.totalArticles.toLocaleString()}篇</strong>，
                    日均服务用户超过<strong>{siteConfig.stats.dailyVisitors}</strong>，
                    累计注册用户突破<strong>{siteConfig.stats.totalUsers}</strong>。
                  </p>
                  <p>
                    久久网的编辑团队由来自各领域的专业人士组成，严格把控内容质量，确保每一篇文章都
                    经过精心编撰和多重审核，为用户提供真实、可靠、有价值的信息服务。
                  </p>
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-background-200/70 p-8 md:p-10 mb-10">
                <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-[var(--font-heading)] mb-6">
                  平台信誉
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                  {[
                    { value: siteConfig.stats.totalArticles.toLocaleString() + '+', label: '精选文章' },
                    { value: siteConfig.stats.totalUsers, label: '累计用户' },
                    { value: siteConfig.stats.dailyVisitors, label: '日访问量' },
                    { value: siteConfig.stats.categories + '大', label: '内容分类' },
                  ].map((item) => (
                    <div key={item.label} className="text-center p-6 bg-background-50 rounded-xl">
                      <div className="text-2xl md:text-3xl font-bold text-primary-500 mb-2">{item.value}</div>
                      <div className="text-sm text-foreground-500">{item.label}</div>
                    </div>
                  ))}
                </div>

                <div className="space-y-4 text-sm md:text-base text-foreground-700 leading-relaxed">
                  <p>
                    久久网高度重视平台信誉建设，严格遵循国家互联网信息服务管理规定，
                    坚持合法合规运营。我们建立了完善的内容审核机制和用户反馈体系，
                    确保平台内容的真实性、准确性和安全性。
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6">
                    {[
                      { icon: 'ri-shield-check-line', title: '内容真实可靠', desc: '多道审核流程，确保信息准确' },
                      { icon: 'ri-lock-line', title: '用户隐私保护', desc: '严格的信息安全管理体系' },
                      { icon: 'ri-customer-service-2-line', title: '优质服务体验', desc: '7×24小时全天候服务支持' },
                    ].map((item) => (
                      <div key={item.title} className="flex items-start gap-3 p-4 bg-background-50 rounded-xl">
                        <div className="w-10 h-10 rounded-lg bg-primary-100/70 flex items-center justify-center flex-shrink-0">
                          <i className={`${item.icon} text-primary-500 text-lg`}></i>
                        </div>
                        <div>
                          <h4 className="text-sm font-semibold text-foreground-900">{item.title}</h4>
                          <p className="text-xs text-foreground-500 mt-0.5">{item.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-background-200/70 p-8 md:p-10">
                <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-[var(--font-heading)] mb-6">
                  联系我们
                </h2>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                  <div>
                    <p className="text-sm text-foreground-600 mb-6">
                      如果您有任何问题、建议或合作意向，欢迎通过以下方式联系我们，或直接填写右侧表单，
                      我们会在第一时间回复您。
                    </p>
                    <div className="space-y-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-accent-100/80 flex items-center justify-center">
                          <i className="ri-mail-line text-accent-600 text-lg"></i>
                        </div>
                        <div>
                          <p className="text-xs text-foreground-400">电子邮箱</p>
                          <p className="text-sm font-medium text-foreground-800">{siteConfig.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-primary-100/70 flex items-center justify-center">
                          <i className="ri-phone-line text-primary-500 text-lg"></i>
                        </div>
                        <div>
                          <p className="text-xs text-foreground-400">客服电话</p>
                          <p className="text-sm font-medium text-foreground-800">{siteConfig.phone}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-secondary-100/80 flex items-center justify-center">
                          <i className="ri-map-pin-line text-secondary-600 text-lg"></i>
                        </div>
                        <div>
                          <p className="text-xs text-foreground-400">公司地址</p>
                          <p className="text-sm font-medium text-foreground-800">{siteConfig.address}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <form
                      onSubmit={handleSubmit}
                      data-readdy-form="true"
                      className="space-y-4"
                    >
                      <div>
                        <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          您的姓名
                        </label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          required
                          className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors"
                          placeholder="请输入姓名"
                        />
                      </div>
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          电子邮箱
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          required
                          className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors"
                          placeholder="请输入邮箱"
                        />
                      </div>
                      <div>
                        <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          主题
                        </label>
                        <input
                          type="text"
                          id="subject"
                          name="subject"
                          required
                          className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors"
                          placeholder="请输入主题"
                        />
                      </div>
                      <div>
                        <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          留言内容
                        </label>
                        <textarea
                          id="message"
                          name="message"
                          required
                          maxLength={500}
                          rows={4}
                          className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors resize-none"
                          placeholder="请输入留言内容（最多500字）"
                        ></textarea>
                      </div>
                      <div className="phone-alt-container" style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, overflow: 'hidden' }}>
                        <input
                          type="text"
                          name="phone_alt"
                          tabIndex={-1}
                          autoComplete="off"
                          aria-hidden="true"
                          readOnly
                        />
                      </div>
                      <button
                        type="submit"
                        className="w-full px-6 py-3 bg-primary-500 text-white text-sm font-medium rounded-lg hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                      >
                        提交留言
                      </button>
                      {formStatus === 'success' && (
                        <p className="text-sm text-green-600 text-center">{formMessage}</p>
                      )}
                      {formStatus === 'error' && (
                        <p className="text-sm text-red-500 text-center">{formMessage}</p>
                      )}
                    </form>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}